// Shared report bodies for the 3 report pages (ReportBillableSupplier.js,
// ReportBillableClient.js, ReportRevenue.js). Not a page itself — each of these
// fetches its own data, so per src/pages/CLAUDE.md's own convention these don't
// belong in src/components/ (that library is for presentational-only pieces).
// Extracted from the old Reports.js when its 3 tabs became 3 separate routes.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

export const fmt$ = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—';
export const fmtN = (v) => v != null ? Number(v).toLocaleString() : '—';

export const STATUS_COLORS = {
  'Pending Kickoff': '#e67e22', Spinup: '#3498db', WIP: '#9b59b6',
  Close: '#7f8c8d', Completed: '#27ae60',
};
export const INV_STATUS_COLORS = { Forecast: '#e67e22', Invoiced: '#3498db', Paid: '#27ae60' };

export const fmtMonth = (m) => {
  if (!m) return '—';
  const [yr, mo] = m.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(mo) - 1]} '${yr.slice(2)}`;
};

// ── Date filter bar (shared) ──────────────────────────────────────────────────
export function DateFilterBar({ dateFrom, dateTo, onDateFrom, onDateTo, onApply }) {
  const setRange = (from, to) => { onDateFrom(from); onDateTo(to); };
  const btnStyle = { padding: '7px 12px', background: '#ecf0f1', border: '1px solid #bdc3c7', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#2c3e50' };
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
      <div>
        <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '3px' }}>Date From</label>
        <input type="date" value={dateFrom} onChange={e => onDateFrom(e.target.value)} style={{ padding: '7px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }} />
      </div>
      <div>
        <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '3px' }}>Date To</label>
        <input type="date" value={dateTo} onChange={e => onDateTo(e.target.value)} style={{ padding: '7px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }} />
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {[
          { label: 'This Month', fn: () => { const n = new Date(); setRange(new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0,10), new Date(n.getFullYear(), n.getMonth()+1, 0).toISOString().slice(0,10)); }},
          { label: 'Last Month', fn: () => { const n = new Date(); setRange(new Date(n.getFullYear(), n.getMonth()-1, 1).toISOString().slice(0,10), new Date(n.getFullYear(), n.getMonth(), 0).toISOString().slice(0,10)); }},
          { label: 'This Year',  fn: () => { const yr = new Date().getFullYear(); setRange(`${yr}-01-01`, `${yr}-12-31`); }},
          { label: 'All Time',   fn: () => setRange('', '') },
        ].map(({ label, fn }) => <button key={label} onClick={fn} style={btnStyle}>{label}</button>)}
      </div>
      <button onClick={onApply} style={{ padding: '8px 18px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Apply</button>
    </div>
  );
}

// ── Billable Leads (by Supplier or Client) ────────────────────────────────────
export function BillableLeadsReport({ groupBy }) {
  const endpoint = groupBy === 'supplier' ? 'billable-leads-by-supplier' : 'billable-leads-by-client';
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo)   params.set('date_to',   dateTo);
    axios.get(`${API}/reports/${endpoint}?${params}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateFrom, dateTo, endpoint]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const handleDownload = () => {
    if (!data) return;
    const { months, data: groups } = data;
    const label = groupBy === 'supplier' ? 'Supplier' : 'Client';
    const headers = [label, 'Qty Needed', ...months.map(fmtMonth), 'Total CA'];
    const wsData = [headers];
    for (const g of groups) {
      wsData.push([g.name, '', ...months.map(m => g.by_month[m] || ''), g.total]);
      for (const p of g.programs || []) {
        wsData.push(['  ' + (p.program_name || '—'), '', ...months.map(m => p.by_month[m] || ''), p.total]);
        for (const c of p.components || []) {
          wsData.push(['    ' + (c.component_name || '—'), c.qty_ordered || '', ...months.map(m => c.by_month[m] || ''), c.total]);
          for (const goal of c.goals || []) {
            wsData.push(['      ' + (goal.goal_name || '—'), goal.qty_ordered || '', ...months.map(m => goal.by_month[m] || ''), goal.total]);
          }
        }
      }
    }
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Billable Leads by ${label}`);
    XLSX.writeFile(wb, `billable-leads-by-${groupBy}.xlsx`);
  };

  if (loading) return <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>Loading…</div>;

  const { months = [], data: groups = [] } = data || {};

  const th  = { padding: '8px 10px', textAlign: 'left',  borderBottom: '2px solid #ddd', fontSize: '12px', background: '#f5f5f5', whiteSpace: 'nowrap' };
  const thr = { ...th, textAlign: 'right' };
  const td  = { padding: '6px 10px', borderBottom: '1px solid #eee', fontSize: '12px' };
  const tdr = { ...td, textAlign: 'right' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <DateFilterBar dateFrom={dateFrom} dateTo={dateTo} onDateFrom={setDateFrom} onDateTo={setDateTo} onApply={() => { setCollapsed({}); load(); }} />
        <button onClick={handleDownload} style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap', marginLeft: '12px' }}>
          ↓ Download XLSX
        </button>
      </div>

      {groups.length === 0 ? (
        <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>No Client Accepted leads for this period.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, minWidth: 220 }}>Name</th>
                <th style={thr}>Qty Needed</th>
                {months.map(m => <th key={m} style={thr}>{fmtMonth(m)}</th>)}
                <th style={{ ...thr, fontWeight: '700' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                const gKey = `g-${g.id}`;
                const gOpen = !collapsed[gKey];
                return (
                  <React.Fragment key={gKey}>
                    {/* Group row */}
                    <tr style={{ background: '#2c3e50', color: 'white', cursor: 'pointer' }} onClick={() => toggle(gKey)}>
                      <td style={{ ...td, color: 'white', fontWeight: '700', paddingLeft: 12 }}>
                        <span style={{ marginRight: 8, fontSize: 10 }}>{gOpen ? '▼' : '▶'}</span>{g.name}
                      </td>
                      <td style={{ ...tdr, color: 'white' }}></td>
                      {months.map(m => <td key={m} style={{ ...tdr, color: 'white' }}>{g.by_month[m] ? fmtN(g.by_month[m]) : '—'}</td>)}
                      <td style={{ ...tdr, color: 'white', fontWeight: '700' }}>{fmtN(g.total)}</td>
                    </tr>

                    {gOpen && (g.programs || []).map(p => {
                      const pKey = `p-${p.program_id}`;
                      const pOpen = !collapsed[pKey];
                      return (
                        <React.Fragment key={pKey}>
                          {/* Program row */}
                          <tr style={{ background: '#eaf4fb', cursor: 'pointer' }} onClick={() => toggle(pKey)}>
                            <td style={{ ...td, paddingLeft: 28, fontWeight: '600' }}>
                              <span style={{ marginRight: 8, fontSize: 10, color: '#3498db' }}>{pOpen ? '▼' : '▶'}</span>{p.program_name || '—'}
                            </td>
                            <td style={tdr}></td>
                            {months.map(m => <td key={m} style={tdr}>{p.by_month[m] ? fmtN(p.by_month[m]) : '—'}</td>)}
                            <td style={{ ...tdr, fontWeight: '600' }}>{fmtN(p.total)}</td>
                          </tr>

                          {pOpen && (p.components || []).map(c => {
                            const cKey = `c-${c.component_id}`;
                            const cOpen = !collapsed[cKey];
                            return (
                              <React.Fragment key={cKey}>
                                {/* Component row */}
                                <tr style={{ background: '#f9f9f9', cursor: 'pointer' }} onClick={() => toggle(cKey)}>
                                  <td style={{ ...td, paddingLeft: 46 }}>
                                    <span style={{ marginRight: 8, fontSize: 10, color: '#7f8c8d' }}>{cOpen ? '▼' : '▶'}</span>{c.component_name || '—'}
                                  </td>
                                  <td style={{ ...tdr, color: '#7f8c8d' }}>{c.qty_ordered != null ? fmtN(c.qty_ordered) : '—'}</td>
                                  {months.map(m => <td key={m} style={tdr}>{c.by_month[m] ? fmtN(c.by_month[m]) : '—'}</td>)}
                                  <td style={tdr}>{fmtN(c.total)}</td>
                                </tr>

                                {cOpen && (c.goals || []).map(goal => (
                                  /* Goal row */
                                  <tr key={goal.goal_id} style={{ background: 'white' }}>
                                    <td style={{ ...td, paddingLeft: 64, color: '#555' }}>{goal.goal_name || '—'}</td>
                                    <td style={{ ...tdr, color: '#7f8c8d' }}>{goal.qty_ordered != null ? fmtN(goal.qty_ordered) : '—'}</td>
                                    {months.map(m => <td key={m} style={{ ...tdr, color: (goal.by_month[m] || 0) > 0 ? '#27ae60' : '#ccc' }}>{goal.by_month[m] ? fmtN(goal.by_month[m]) : '—'}</td>)}
                                    <td style={{ ...tdr, fontWeight: '600', color: goal.total > 0 ? '#27ae60' : '#aaa' }}>{fmtN(goal.total)}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Revenue Recognition ───────────────────────────────────────────────────────

// Grand totals across the program rows only — the invoice sub-rows under each
// program are children of those figures, so summing them too would double-count.
// Used by both the on-screen totals row and the XLSX export so the two can't drift.
export function revenueTotals(programs, months) {
  const sum = (pick) => programs.reduce((acc, p) => acc + (Number(pick(p)) || 0), 0);
  const totals = {
    total_revenue:  sum(p => p.total_revenue),
    total_invoiced: sum(p => p.total_invoiced),
    total_paid:     sum(p => p.total_paid),
    approved_leads: sum(p => p.approved_leads),
    by_month:       {},
  };
  for (const m of months) totals.by_month[m] = sum(p => p.by_month?.[m]);
  totals.reconciliation = totals.total_invoiced - totals.total_revenue;
  return totals;
}

export function RevenueReport() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/reports/revenue-recognition`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  const handleDownload = () => {
    if (!data) return;
    const { months, data: programs } = data;
    const headers = ['Program', 'PO #', 'Status', 'Total Revenue', ...months.map(fmtMonth), 'Total Invoiced', 'Total Paid', 'CA Leads', 'Reconciliation'];
    const n = months.length;
    const C_REVENUE  = 3;              // Total Revenue
    const C_MONTH0   = 4;              // first month column
    const C_INVOICED = 4 + n;
    const C_PAID     = 5 + n;
    const C_LEADS    = 6 + n;
    const C_RECON    = 7 + n;

    const wsData = [headers];
    // Same grand-total row the table shows, in the same position (directly under
    // the header) so the export matches what's on screen.
    const t = revenueTotals(programs, months);
    const hasRows = programs.length > 0;
    if (hasRows) {
      wsData.push(['All Programs', '', '', t.total_revenue,
        ...months.map(m => t.by_month[m] || 0), t.total_invoiced, t.total_paid, t.approved_leads, t.reconciliation]);
    }
    // Track where each program landed so the formulas below can point at real
    // cells: a program's own figures are sums of its invoice sub-rows, and the
    // totals row is a sum of the program rows (skipping the sub-rows, which
    // would otherwise be counted twice).
    const programRows  = [];   // 1-indexed sheet row of each program row
    const invoiceSpans = [];   // {first,last} 1-indexed sub-row span, or null
    for (const p of programs) {
      const recon = (p.total_invoiced || 0) - (p.total_revenue || 0);
      wsData.push([p.program_name || '—', p.po_number || '—', p.status || '—', p.total_revenue || 0,
        ...months.map(m => p.by_month[m] || ''), p.total_invoiced || 0, p.total_paid || 0, p.approved_leads || 0,
        recon]);
      programRows.push(wsData.length);
      const invs = p.invoices || [];
      const first = wsData.length + 1;
      for (const inv of invs) {
        wsData.push([`  ${inv.invoice_number || '(no #)'}`, '', inv.status || '', '',
          ...months.map(m => m === inv.month ? (inv.invoice_amount || 0) : ''),
          inv.invoice_amount || 0, inv.status === 'Paid' ? (inv.invoice_amount || 0) : '', '', '']);
      }
      invoiceSpans.push(invs.length ? { first, last: first + invs.length - 1 } : null);
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    // Replace the computed cells with live formulas, keeping the value we already
    // calculated as the cached result so the numbers are right even before Excel
    // recalculates. Source data (Total Revenue, CA Leads, each invoice's own
    // amount) stays as plain values — there's nothing to derive it from.
    const col   = (c) => XLSX.utils.encode_col(c);
    const setF  = (c, r, f, v) => { ws[XLSX.utils.encode_cell({ c, r: r - 1 })] = { t: 'n', f, v }; };
    const sumOf = (c, rows) => `SUM(${rows.map(r => `${col(c)}${r}`).join(',')})`;

    programs.forEach((p, i) => {
      const r    = programRows[i];
      const span = invoiceSpans[i];
      if (span) {
        const range = (c) => `SUM(${col(c)}${span.first}:${col(c)}${span.last})`;
        months.forEach((m, k) => {
          // Only where there's actually an invoice that month — an empty month
          // cell stays blank rather than becoming a noisy 0.00.
          if (p.by_month[m]) setF(C_MONTH0 + k, r, range(C_MONTH0 + k), p.by_month[m]);
        });
        setF(C_INVOICED, r, range(C_INVOICED), p.total_invoiced || 0);
        setF(C_PAID,     r, range(C_PAID),     p.total_paid || 0);
      }
      setF(C_RECON, r, `${col(C_INVOICED)}${r}-${col(C_REVENUE)}${r}`, (p.total_invoiced || 0) - (p.total_revenue || 0));
    });

    if (hasRows) {
      setF(C_REVENUE, 2, sumOf(C_REVENUE, programRows), t.total_revenue);
      months.forEach((m, k) => setF(C_MONTH0 + k, 2, sumOf(C_MONTH0 + k, programRows), t.by_month[m] || 0));
      setF(C_INVOICED, 2, sumOf(C_INVOICED, programRows), t.total_invoiced);
      setF(C_PAID,     2, sumOf(C_PAID,     programRows), t.total_paid);
      setF(C_LEADS,    2, sumOf(C_LEADS,    programRows), t.approved_leads);
      setF(C_RECON,    2, `${col(C_INVOICED)}2-${col(C_REVENUE)}2`, t.reconciliation);
    }

    // Number formats + column widths. These are the only presentation features
    // the Apache-licensed xlsx build actually writes — cell fills, fonts and
    // freeze panes are SheetJS Pro features and get silently dropped, so the
    // download can't be colour-matched to the on-screen report without swapping
    // libraries. Money still reads as money rather than a raw float.
    const MONEY = '$#,##0.00';
    const fmtCol = (c, z) => {
      for (let r = 2; r <= wsData.length; r++) {
        const cell = ws[XLSX.utils.encode_cell({ c, r: r - 1 })];
        if (cell && cell.t === 'n') cell.z = z;
      }
    };
    [C_REVENUE, ...months.map((_, k) => C_MONTH0 + k), C_INVOICED, C_PAID, C_RECON]
      .forEach(c => fmtCol(c, MONEY));
    fmtCol(C_LEADS, '#,##0');
    ws['!cols'] = [
      { wch: 34 },                        // Program name / indented invoice number
      { wch: 14 },                        // PO # / invoice date
      { wch: 18 },                        // Status
      { wch: 15 },                        // Total Revenue
      ...months.map(() => ({ wch: 14 })),
      { wch: 15 },                        // Total Invoiced
      { wch: 14 },                        // Total Paid
      { wch: 11 },                        // CA Leads
      { wch: 15 },                        // Reconciliation
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Revenue Recognition');
    XLSX.writeFile(wb, 'revenue-recognition.xlsx');
  };

  if (loading) return <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>Loading…</div>;

  const { months = [], data: programs = [] } = data || {};

  const th  = { padding: '8px 10px', textAlign: 'left',  borderBottom: '2px solid #ddd', fontSize: '12px', background: '#f5f5f5', whiteSpace: 'nowrap' };
  const thr = { ...th, textAlign: 'right' };
  const td  = { padding: '6px 10px', borderBottom: '1px solid #eee', fontSize: '12px' };
  const tdr = { ...td, textAlign: 'right' };

  const totals   = revenueTotals(programs, months);
  const totalTd  = { ...td, color: 'white', fontWeight: '700', borderBottom: '2px solid #1a252f' };
  const totalTdr = { ...totalTd, textAlign: 'right' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button onClick={handleDownload} style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
          ↓ Download XLSX
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, minWidth: 200 }}></th>
              <th style={th}>PO #</th>
              <th style={th}>Status</th>
              <th style={thr}>Total Revenue</th>
              {months.map(m => <th key={m} style={thr}>{fmtMonth(m)}</th>)}
              <th style={thr}>Total Invoiced</th>
              <th style={thr}>Total Paid</th>
              <th style={thr}>CA Leads</th>
              <th style={thr}>Reconciliation</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {/* Grand total across every program, pinned at the top of the table */}
            {programs.length > 0 && (
              <tr style={{ background: '#2c3e50' }}>
                <td style={{ ...totalTd, paddingLeft: 10 }}>All Programs</td>
                <td style={totalTd}></td>
                <td style={totalTd}></td>
                <td style={totalTdr}>{fmt$(totals.total_revenue)}</td>
                {months.map(m => <td key={m} style={totalTdr}>{totals.by_month[m] ? fmt$(totals.by_month[m]) : '—'}</td>)}
                <td style={totalTdr}>{fmt$(totals.total_invoiced)}</td>
                <td style={totalTdr}>{fmt$(totals.total_paid)}</td>
                <td style={totalTdr}>{fmtN(totals.approved_leads)}</td>
                <td style={{ ...totalTdr, color: totals.reconciliation < 0 ? '#ff7675' : '#55efc4' }}>
                  {(totals.reconciliation >= 0 ? '+' : '') + fmt$(totals.reconciliation)}
                </td>
                <td style={totalTd}></td>
              </tr>
            )}
            {programs.map(p => {
              const isOpen = !collapsed[p.program_id];
              const recon  = (p.total_invoiced || 0) - (p.total_revenue || 0);
              return (
                <React.Fragment key={p.program_id}>
                  <tr style={{ background: '#f0f4f8', cursor: 'pointer', fontWeight: '600' }} onClick={() => toggle(p.program_id)}>
                    <td style={{ ...td, paddingLeft: 10 }}>
                      <span style={{ marginRight: 8, fontSize: 10, color: '#3498db' }}>{isOpen ? '▼' : '▶'}</span>
                      {p.program_name || '—'}
                    </td>
                    <td style={td}>{p.po_number || '—'}</td>
                    <td style={td}>
                      {p.status
                        ? <span style={{ background: STATUS_COLORS[p.status] || '#95a5a6', color: 'white', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>{p.status}</span>
                        : '—'}
                    </td>
                    <td style={tdr}>{fmt$(p.total_revenue)}</td>
                    {months.map(m => <td key={m} style={{ ...tdr, color: p.by_month[m] ? '#2c3e50' : '#ccc' }}>{p.by_month[m] ? fmt$(p.by_month[m]) : '—'}</td>)}
                    <td style={tdr}>{fmt$(p.total_invoiced)}</td>
                    <td style={tdr}>{fmt$(p.total_paid)}</td>
                    <td style={{ ...tdr, color: p.approved_leads > 0 ? '#27ae60' : '#aaa' }}>{fmtN(p.approved_leads)}</td>
                    <td style={{ ...tdr, color: recon < 0 ? '#e74c3c' : '#27ae60' }}>
                      {p.total_invoiced != null ? (recon >= 0 ? '+' : '') + fmt$(recon) : '—'}
                    </td>
                    <td style={td}></td>
                  </tr>
                  {isOpen && (p.invoices || []).map(inv => (
                    <tr key={inv.id} style={{ background: 'white' }}>
                      <td style={{ ...td, paddingLeft: 32, color: '#555' }}>
                        {inv.invoice_number || '(no #)'}
                        {inv.notes && <span style={{ color: '#95a5a6', marginLeft: 8, fontStyle: 'italic', fontSize: 11 }}>{inv.notes}</span>}
                      </td>
                      <td style={{ ...td, fontSize: '11px', color: '#7f8c8d' }}>{inv.invoice_date || '—'}</td>
                      <td style={td}>
                        {inv.status
                          ? <span style={{ background: INV_STATUS_COLORS[inv.status] || '#95a5a6', color: 'white', borderRadius: '4px', padding: '1px 7px', fontSize: '11px' }}>{inv.status}</span>
                          : '—'}
                      </td>
                      <td style={tdr}></td>
                      {months.map(m => <td key={m} style={{ ...tdr, color: '#3498db' }}>{m === inv.month && inv.invoice_amount != null ? fmt$(inv.invoice_amount) : ''}</td>)}
                      <td style={tdr}>{fmt$(inv.invoice_amount)}</td>
                      <td style={tdr}>{inv.status === 'Paid' ? fmt$(inv.invoice_amount) : '—'}</td>
                      <td style={tdr}></td>
                      <td style={tdr}></td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button
                          onClick={() => navigate('/invoices', { state: { editInvoiceId: inv.id } })}
                          style={{ padding: '3px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                        >Edit</button>
                      </td>
                    </tr>
                  ))}
                  {isOpen && (p.invoices || []).length === 0 && (
                    <tr><td colSpan={9 + months.length} style={{ ...td, paddingLeft: 32, color: '#aaa', fontStyle: 'italic' }}>No invoices</td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {programs.length === 0 && (
              <tr><td colSpan={9 + months.length} style={{ ...td, textAlign: 'center', color: '#aaa', padding: '40px' }}>No data available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
